import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";

import { useApiCall } from "@framework/react-hooks";

import type {
  IComponentFaultFrequencyApi,
  IFaultFrequencyChartCursor,
} from "../../scheme/components/analytics/interfaces";
import {
  buildFaultFrequencyColorMap,
  filterValidFaultLineKeys,
} from "../../scheme/components/analytics/utils/line-colors";
import { resolveDataMarkMeasureDate } from "../utils/data-marks";
import { MULTI_ANALYSIS_MEASURE_TYPE, type IMultiAnalysisDataMark } from "../types";
import { resolveTrainComponentIdForPoint } from "../utils/point-component-map";

interface IUseMultiAnalysisFaultFrequenciesArgs {
  enabled: boolean;
  trainId: number | null;
  isPanelOpen: boolean;
  onPanelOpenChange: (open: boolean) => void;
  effectiveDataMarks: readonly IMultiAnalysisDataMark[];
  activeMeasureType: MULTI_ANALYSIS_MEASURE_TYPE | null;
}

const buildSelectedFaultFrequenciesValues = (
  faultFrequencies: IComponentFaultFrequencyApi[] | null,
  selectedFaultLineKeys: readonly string[],
): IFaultFrequencyChartCursor[] | undefined => {
  if (!faultFrequencies?.length || !selectedFaultLineKeys.length) {
    return undefined;
  }

  const colorMap = buildFaultFrequencyColorMap(faultFrequencies);
  const out: IFaultFrequencyChartCursor[] = [];

  const addGroup = (hzList: (number | undefined)[], color: string) => {
    for (const value of hzList) {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        out.push({ frequencyHz: value, color });
      }
    }
  };

  for (const fault of faultFrequencies) {
    const parentKey = `fault-code-${fault.code}`;

    if (selectedFaultLineKeys.includes(parentKey)) {
      const color = colorMap.get(parentKey)!;

      if (fault.lines?.length) {
        addGroup(
          fault.lines.map(line => line.frequencyHz),
          color,
        );
      } else {
        addGroup([fault.frequencyHz], color);
      }

      continue;
    }

    if (fault.lines?.length) {
      for (const line of fault.lines) {
        if (selectedFaultLineKeys.includes(line.lineKey)) {
          addGroup([line.frequencyHz], colorMap.get(line.lineKey)!);
        }
      }
    } else if (fault.freqCode && selectedFaultLineKeys.includes(fault.freqCode)) {
      addGroup([fault.frequencyHz], colorMap.get(String(fault.freqCode))!);
    }
  }

  return out.length ? out : undefined;
};

export const useMultiAnalysisFaultFrequencies = ({
  enabled,
  trainId,
  isPanelOpen,
  onPanelOpenChange,
  effectiveDataMarks,
  activeMeasureType,
}: IUseMultiAnalysisFaultFrequenciesArgs) => {
  const { enqueueSnackbar } = useSnackbar();
  const [faultFrequencies, setFaultFrequencies] = useState<IComponentFaultFrequencyApi[] | null>(null);
  const [selectedFaultLineKeys, setSelectedFaultLineKeys] = useState<string[]>([]);
  const isPanelOpenRef = useRef(isPanelOpen);

  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen;
  }, [isPanelOpen]);

  const activeDataMark = effectiveDataMarks[0] ?? null;
  const dataMarkFingerprint = activeDataMark
    ? `${activeDataMark.idMeasure}:${activeDataMark.date}:${activeDataMark.idPoint}`
    : "";

  const { fn: fetchFaultFrequencies, isLoading } = useApiCall(
    async (
      api,
      params: {
        trainId: number;
        dataMark: IMultiAnalysisDataMark;
      },
    ): Promise<IComponentFaultFrequencyApi[]> => {
      const idTrainComponent = await resolveTrainComponentIdForPoint(api, params.trainId, params.dataMark.idPoint);

      if (idTrainComponent === null) {
        throw new Error("NO_COMPONENT");
      }

      const response = (await api.fetchJson({
        url: "/component/fault-frequencies",
        method: "GET",
        data: {
          idTrainComponent,
          idMeasure: params.dataMark.idMeasure,
          measureDate: resolveDataMarkMeasureDate(params.dataMark),
        },
      })) as IComponentFaultFrequencyApi[] | null | undefined;

      return Array.isArray(response) ? response : [];
    },
    { success: false, error: false },
  );

  const loadFaultFrequencies = useCallback(
    async (options?: { warnOnBlock?: boolean }): Promise<boolean> => {
      const warn = options?.warnOnBlock !== false;

      if (!enabled || trainId === null || activeMeasureType !== MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM) {
        setFaultFrequencies([]);
        onPanelOpenChange(false);
        return false;
      }

      if (!activeDataMark) {
        if (warn) {
          enqueueSnackbar("Для частот неисправностей выберите замер на панели времени.", { variant: "warning" });
        }
        setFaultFrequencies([]);
        onPanelOpenChange(false);
        return false;
      }

      try {
        const response = await fetchFaultFrequencies(undefined, {
          trainId,
          dataMark: activeDataMark,
        });
        const items = response ?? [];

        if (!items.length) {
          if (warn) {
            enqueueSnackbar("Для выбранного замера частоты неисправностей не найдены.", { variant: "warning" });
          }
          setFaultFrequencies([]);
          onPanelOpenChange(false);
          return false;
        }

        setFaultFrequencies(items);
        setSelectedFaultLineKeys(previous => filterValidFaultLineKeys(previous, items));
        return true;
      } catch (error) {
        setFaultFrequencies([]);

        if (error instanceof Error && error.message === "NO_COMPONENT") {
          if (warn) {
            enqueueSnackbar("Для выбранной точки не найден узел агрегата.", { variant: "warning" });
          }
        } else if (warn) {
          enqueueSnackbar("Не удалось загрузить частоты неисправностей.", { variant: "error" });
        }

        onPanelOpenChange(false);
        return false;
      }
    },
    [activeDataMark, activeMeasureType, enabled, enqueueSnackbar, fetchFaultFrequencies, onPanelOpenChange, trainId],
  );

  const loadFaultFrequenciesRef = useRef(loadFaultFrequencies);

  useEffect(() => {
    loadFaultFrequenciesRef.current = loadFaultFrequencies;
  }, [loadFaultFrequencies]);

  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }

    void loadFaultFrequenciesRef.current({ warnOnBlock: false });
  }, [dataMarkFingerprint, isPanelOpen]);

  useEffect(() => {
    if (activeMeasureType !== MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM && isPanelOpenRef.current) {
      onPanelOpenChange(false);
      setSelectedFaultLineKeys([]);
      setFaultFrequencies(null);
    }
  }, [activeMeasureType, onPanelOpenChange]);

  useEffect(() => {
    if (!enabled) {
      onPanelOpenChange(false);
      setSelectedFaultLineKeys([]);
      setFaultFrequencies(null);
    }
  }, [enabled, onPanelOpenChange]);

  const selectedFaultFrequenciesValues = useMemo(
    () => buildSelectedFaultFrequenciesValues(faultFrequencies, selectedFaultLineKeys),
    [faultFrequencies, selectedFaultLineKeys],
  );

  const faultLineColorsByKey = useMemo((): Record<string, string> => {
    if (!faultFrequencies?.length) {
      return {};
    }

    const colorMap = buildFaultFrequencyColorMap(faultFrequencies);
    const colors: Record<string, string> = {};

    for (const fault of faultFrequencies) {
      if (fault.isVisible === false) {
        continue;
      }

      const key = `fault-code-${fault.code}`;
      const color = colorMap.get(key);

      if (color) {
        colors[key] = color;
      }
    }

    return colors;
  }, [faultFrequencies]);

  const toggleFaultFrequenciesPanel = useCallback(async () => {
    if (isPanelOpen) {
      onPanelOpenChange(false);
      setSelectedFaultLineKeys([]);
      return;
    }

    const loaded = await loadFaultFrequencies({ warnOnBlock: true });

    if (loaded) {
      onPanelOpenChange(true);
    }
  }, [isPanelOpen, loadFaultFrequencies, onPanelOpenChange]);

  const showFaultFrequenciesPanel =
    isPanelOpen &&
    activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM &&
    Boolean(faultFrequencies?.length || isLoading);

  return {
    faultFrequencies,
    selectedFaultLineKeys,
    setSelectedFaultLineKeys,
    selectedFaultFrequenciesValues,
    faultLineColorsByKey,
    isFaultFrequenciesLoading: isLoading,
    toggleFaultFrequenciesPanel,
    showFaultFrequenciesPanel,
  };
};
